import { useCallback, useEffect, useMemo } from "react";

import { ChevronLeft } from "@mui/icons-material";
import { Typography, Box, useTheme, Stack, IconButton, Button } from "@mui/material";

import { useAppSelector } from "@app/hooks";

import { ReactComponent as GawainIcon } from "@assets/icons/gawain_ai.svg";
import KnightImg from "@assets/images/knight.png";

import { useGawainAssistant } from "@contexts/GawainAssistantContext";

import { Conversations } from "./Conversations";
import CurrentChat from "./CurrentChat";

interface Props {
  showListFirst?: boolean;
  onBack?: () => void;
  containerStyle?: React.CSSProperties;
}

const AssistantChat = ({ showListFirst, containerStyle, onBack }: Props) => {
  const { user } = useAppSelector((state) => state.auth.userInfo);
  const {
    currentSession,
    setCurrentSession,
    conversations,
    loadingConversations,
    loadingMessages,
  } = useGawainAssistant();

  const {
    palette: { common },
  } = useTheme();

  const sortedConversations = useMemo(
    () =>
      !!conversations?.length
        ? [...conversations].sort(
            (a, b) => b.session - a.session // Latest session first
          )
        : [],
    [conversations]
  );

  const openChatDetail = useCallback(
    async (conversationId?: string) => {
      if (conversationId) {
        setCurrentSession(conversationId);
        return;
      }
      const newConversationId = `${user?.id}-assistant-${Date.now()}`;
      setCurrentSession(newConversationId);
    },
    [user, setCurrentSession]
  );

  const closeCurrentChat = () => {
    setCurrentSession(null);
  };

  const handleClickBack = () => {
    if (currentSession) {
      closeCurrentChat();
    } else if (onBack) {
      onBack();
    }
  };

  useEffect(() => {
    if (user && !showListFirst) {
      openChatDetail();
    }
  }, [user, showListFirst]);

  return (
    <>
      <Box
        position={"absolute"}
        top={"-200px"}
        right={"-220px"}
        width={{ xs: "100%", md: "446px" }}
      >
        <img src={KnightImg} alt="" />
      </Box>
      <Box
        position="sticky"
        top={92}
        width={"100%"}
        display="flex"
        flexDirection="column"
        height={"80vh"}
        border={`1px solid ${common.bellflowerBlue}`}
        overflow={"hidden"}
        borderRadius={2}
        sx={{
          "&.css-dev-only-make-styles-popper": {
            position: "fixed",
          },
          ...containerStyle,
        }}
      >
        <Stack
          display={"flex"}
          flex={1}
          height={"100%"}
          overflow={"hidden"}
          sx={{ "&::-webkit-scrollbar": { display: "none" } }}
        >
          <Box
            display={"flex"}
            height={66}
            alignItems={"center"}
            justifyContent={"space-between"}
            p={"12px"}
            borderBottom={`1px solid ${common.bellflowerBlue}`}
            zIndex={1}
          >
            <Box display={"flex"} alignItems={"center"} gap={1}>
              {(!!currentSession || !!onBack) && (
                <IconButton onClick={handleClickBack} sx={{ cursor: "pointer" }}>
                  <ChevronLeft />
                </IconButton>
              )}
              <Stack flexDirection={"row"} gap={"8px"} alignItems={"center"}>
                <GawainIcon width={50} height={50} />
                <Typography ml={1} fontWeight={700} fontSize={20}>
                  Gawain.AI
                </Typography>
              </Stack>
            </Box>
            {!currentSession && (
              <Button
                sx={{
                  lineHeight: "14px",
                  borderRadius: "8px",
                  fontSize: "12px",
                }}
                onClick={() => openChatDetail()}
              >
                New chat
              </Button>
            )}
          </Box>

          <Box
            flex={1}
            overflow={"auto"}
            sx={{
              "&::-webkit-scrollbar": { display: "none" },
            }}
            position={"relative"}
          >
            <Box p={"12px"} sx={{ "&::-webkit-scrollbar": { display: "none" } }}>
              {!currentSession && (
                <Conversations
                  data={sortedConversations}
                  loading={loadingMessages || loadingConversations}
                  onClick={openChatDetail}
                />
              )}
            </Box>
            <Box
              position={"absolute"}
              top={0}
              left={currentSession ? 0 : "100%"}
              width={"100%"}
              height={"100%"}
              overflow={"hidden"}
              sx={{
                transition: "left 0.3s ease-in-out",
              }}
            >
              {currentSession && <CurrentChat />}
            </Box>
          </Box>
        </Stack>
      </Box>
    </>
  );
};

export { AssistantChat };
